/**
 * Composable for receiving Pyxel-native screencast / screenshot blobs from
 * the iframe via the `_savePyxelFile` override (#374).
 *
 * ## How it works
 *
 * `PyxelPlayer.vue` injects a setter trap on `window._savePyxelFile` in the
 * iframe (when `capture` prop is true). When Pyxel writes a GIF (.gif) or PNG
 * (.png) to its virtual FS, the override reads it back via `pyodide.FS.readFile`,
 * wraps it as a Blob, and posts it to the parent frame:
 *
 *     window.parent.postMessage({
 *       type: '__pyxelCapture',
 *       kind: 'screencast' | 'screenshot',
 *       filename: string,
 *       blob: Blob,
 *     }, '*')
 *
 * This composable listens for those messages and accumulates the blobs.
 *
 * #959: when Pyxel has recorded **zero frames** (the work opted out with
 * `pyxel.init(capture_sec=0)`, or the game was paused from boot) it writes no
 * GIF but still calls `_savePyxelFile`. The override then posts
 * `{ type: '__pyxelCapture', kind: 'screencast', filename, empty: true }` (no
 * blob). It rides the same message stream so the #707 screencast[i] ↔
 * screenshot[i] pairing is preserved; the dialog substitutes the paired still.
 *
 * If Pyodide dies (fatal), the frame posts `{ type: '__pyxelFatal' }` and this
 * composable stops the cadence (`pyxelFailed` becomes true). Poking a dead
 * Pyodide every tick used to overwrite the real error in Pyxel's overlay.
 *
 * ## Auto-trigger
 *
 * `startCapture(iframe, { intervalMs, triggerMode })` installs a setInterval on
 * the parent that periodically asks Pyxel to flush its rolling screencast buffer
 * to a GIF. Two trigger modes are supported:
 *
 *   - `'runPython'` (default): call `pyodide.runPython('import pyxel;
 *     pyxel.screencast()')` from the parent. Simplest, but may hit Pyodide
 *     reentrance issues if it overlaps with Pyxel's frame loop.
 *   - `'synthesizeKeypress'`: dispatch a synthetic Alt+3 KeyboardEvent on
 *     `iframe.contentWindow.document`. Pyxel's SDL2 layer picks it up and
 *     `check_special_input()` (system.rs:313) invokes `save_screencast()`
 *     within its own frame loop — no Pyodide reentrance risk.
 *
 * Pyxel's `screencast()` flushes its rolling frame buffer to a GIF and
 * auto-resets (screencast.rs:237) — each tick yields one Blob.
 *
 * For hand-triggered captures (Alt+3 in the iframe), this composable receives
 * those too without needing the setInterval.
 *
 * ## Cross-origin driving (#749, Phase 2)
 *
 * When the play iframe is a **cross-origin** sandbox frame (`pyxel-frame.html`,
 * #748) the parent can no longer touch `contentWindow.pyxelContext` — it throws
 * `SecurityError`. Pass `frameOrigin` (the sandbox origin) to `startCapture` and
 * this composable switches to a postMessage command protocol instead:
 *
 *   - `triggerViaRunPython`  → `postMessage({ type:'__pyxelCmd', cmd:'screencast', scale, still, stillScale })`
 *   - `triggerViaKeypress`   → `postMessage({ type:'__pyxelCmd', cmd:'keypress', combo:'alt+3', holdMs })`
 *   - init wait (readiness)  → wait for `{ type:'__pyxelInited' }` from the frame
 *     (the frame monitors its own `pyxelContext.initialized`), rather than polling
 *     `contentWindow.pyxelContext.initialized`.
 *
 * The frame runs the command against its own same-origin pyxelContext; the GIF /
 * PNG blobs still come back as `__pyxelCapture` messages (unchanged). Without
 * `frameOrigin` (same-origin blob fallback) the direct-access path above is used.
 */

type TriggerMode = 'runPython' | 'synthesizeKeypress'

interface CapturedClip {
  /** Absent when `empty` (#959: Pyxel recorded zero frames, so no GIF exists). */
  blob?: Blob
  empty?: boolean
  filename: string
  receivedAt: number
}

interface NativeScreencastOptions {
  /** Interval in ms between auto pyxel.screencast() calls. 0 / undefined → no auto trigger. */
  intervalMs?: number
  /** How to trigger pyxel.screencast() — see composable docs. Default: 'runPython'. */
  triggerMode?: TriggerMode
  /**
   * Hold duration (ms) for the synthesized Alt+3 keypress. Default 50ms is
   * long enough to span at least one Pyxel frame at 30/60 fps. Only used when
   * triggerMode === 'synthesizeKeypress'.
   */
  keypressHoldMs?: number
  /**
   * Scale factor passed to `pyxel.screencast(scale=N)`. Pyxel defaults to
   * `capture_scale=2` (upscaled GIF), which is ~4x slower to encode and ~4x
   * larger than necessary for our use case. Default: 1 (native resolution).
   * Only honored when triggerMode === 'runPython'; the synthesized Alt+3
   * always uses whatever `capture_scale` Pyxel was initialized with.
   */
  scale?: number
  /**
   * #707: when true, pair a native `pyxel.screenshot(scale=2)` (PNG) with each
   * `pyxel.screencast()` trigger so the crystallize flow can confirm an OGP
   * still alongside the chosen GIF clip. Both run in ONE synchronous runPython
   * call — same Pyodide reentrance profile as the screencast-only path — and
   * arrive at the parent as two ordered postMessages (screencast then
   * screenshot), so the dialog can pair `screencasts[i]` with `screenshots[i]`.
   * Only honored when triggerMode === 'runPython'. Default: false.
   */
  captureStill?: boolean
  /** Max number of clips to keep (default 8) */
  maxClips?: number
  /**
   * #749: when set, the play iframe is a cross-origin sandbox frame
   * (`pyxel-frame.html`) served from this origin. The composable then drives
   * capture via postMessage commands (`__pyxelCmd`) and waits for `__pyxelInited`
   * instead of touching `contentWindow.pyxelContext` (which throws cross-origin).
   * Also used to strictly filter the origin of inbound `__pyxelCapture` messages.
   * Undefined → same-origin blob fallback (direct `contentWindow` access).
   */
  frameOrigin?: string
}

/**
 * #707: fixed scale for the OGP still. X's summary card floors images at
 * 144×144px; scale=2 nearest-neighbor keeps dots crisp and clears 144 for any
 * native side ≥72px (72×2=144) — i.e. nearly all works. See issue #707.
 */
const OGP_STILL_SCALE = 2

interface PyxelCaptureMessage {
  type: '__pyxelCapture'
  kind: 'screencast' | 'screenshot'
  filename: string
  blob?: Blob
  /** #959: no file was written (zero recorded frames). */
  empty?: boolean
}

function isPyxelCaptureMessage(data: unknown): data is PyxelCaptureMessage {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    d.type === '__pyxelCapture'
    && (d.kind === 'screencast' || d.kind === 'screenshot')
    && typeof d.filename === 'string'
    && (d.blob instanceof Blob || d.empty === true)
  )
}

/** #959: the frame reports that Pyodide died (fatal) → stop the cadence. */
function isPyxelFatalMessage(data: unknown): boolean {
  return !!data && typeof data === 'object'
    && (data as Record<string, unknown>).type === '__pyxelFatal'
}

/** #749: the cross-origin frame signals Pyxel boot completion with this message. */
function isPyxelInitedMessage(data: unknown): boolean {
  return !!data && typeof data === 'object'
    && (data as Record<string, unknown>).type === '__pyxelInited'
}

interface IframePyxelWindow extends Window {
  pyxelContext?: {
    initialized?: boolean
    hasFatalError?: boolean
    pyodide?: { runPython?: (code: string) => unknown }
  }
}

export function useNativeScreencast(options: NativeScreencastOptions = {}) {
  const { maxClips = 8 } = options

  const screencasts = ref<CapturedClip[]>([])
  const screenshots = ref<CapturedClip[]>([])
  const isCapturing = ref(false)
  /** #959: Pyodide died during capture; the cadence has been stopped. */
  const pyxelFailed = ref(false)

  let iframeEl: HTMLIFrameElement | null = null
  let autoTriggerTimer: ReturnType<typeof setInterval> | null = null
  let initWaitTimer: ReturnType<typeof setInterval> | null = null
  let initWaitTimeout: ReturnType<typeof setTimeout> | null = null
  let messageHandler: ((event: MessageEvent) => void) | null = null
  let activeTriggerMode: TriggerMode = 'runPython'
  let activeKeypressHoldMs = 50
  let activeScale = 1
  let activeCaptureStill = false
  /** #749: sandbox origin when the play iframe is cross-origin; '' → same-origin blob. */
  let activeFrameOrigin = ''
  /** #749: cadence interval pending until the cross-origin frame signals init. */
  let pendingCadenceMs = 0

  /** Drop the oldest clips when the array exceeds maxClips. */
  function trim(arr: CapturedClip[]) {
    if (arr.length > maxClips) arr.splice(0, arr.length - maxClips)
  }

  /** #749: send a driver command to the cross-origin frame (no-op same-origin). */
  function postCmd(payload: Record<string, unknown>) {
    const win = iframeEl?.contentWindow
    if (!win || !activeFrameOrigin) return
    win.postMessage({ type: '__pyxelCmd', ...payload }, activeFrameOrigin)
  }

  /** #959: Pyodide is dead — stop poking it (keeps the message listener). */
  function onPyxelFatal() {
    pyxelFailed.value = true
    if (autoTriggerTimer) {
      clearInterval(autoTriggerTimer)
      autoTriggerTimer = null
    }
  }

  /** #749: start the auto-trigger cadence once (idempotent). */
  function startCadence() {
    if (autoTriggerTimer || pyxelFailed.value || !isCapturing.value || !iframeEl || pendingCadenceMs <= 0) return
    autoTriggerTimer = setInterval(autoTrigger, pendingCadenceMs)
  }

  function onMessage(event: MessageEvent) {
    if (!iframeEl || event.source !== iframeEl.contentWindow) return
    // #749: cross-origin — accept only from the sandbox origin.
    if (activeFrameOrigin && event.origin !== activeFrameOrigin) return
    // #749: the cross-origin frame reports Pyxel boot completion → start cadence.
    if (activeFrameOrigin && isPyxelInitedMessage(event.data)) {
      if (initWaitTimeout) {
        clearTimeout(initWaitTimeout)
        initWaitTimeout = null
      }
      startCadence()
      return
    }
    if (isPyxelFatalMessage(event.data)) {
      onPyxelFatal()
      return
    }
    if (!isPyxelCaptureMessage(event.data)) return
    const clip: CapturedClip = {
      blob: event.data.blob,
      empty: event.data.empty === true,
      filename: event.data.filename,
      receivedAt: Date.now(),
    }
    if (event.data.kind === 'screencast') {
      screencasts.value = [...screencasts.value, clip]
      trim(screencasts.value)
    }
    else {
      screenshots.value = [...screenshots.value, clip]
      trim(screenshots.value)
    }
  }

  /** Mode A: ask Pyodide to call pyxel.screencast() directly. */
  function triggerViaRunPython() {
    // #749 cross-origin: delegate to the frame via a command (it runs the same
    // runPython against its own pyxelContext). Same #707 screencast+screenshot pairing.
    if (activeFrameOrigin) {
      postCmd({ cmd: 'screencast', scale: activeScale, still: activeCaptureStill, stillScale: OGP_STILL_SCALE })
      return
    }
    const ctx = (iframeEl?.contentWindow as IframePyxelWindow | null)?.pyxelContext
    if (!ctx?.initialized) return
    if (ctx.hasFatalError) {
      onPyxelFatal()
      return
    }
    try {
      // #959: touching a dead Pyodide's properties throws, so the guard lives inside the try.
      if (!ctx.pyodide?.runPython) return
      const scaleArg = activeScale >= 1 ? `scale=${Math.floor(activeScale)}` : ''
      // #707: optionally pair a native screenshot (PNG, scale=2) in the SAME
      // runPython call — screencast first (its GIF blob), then screenshot (its
      // PNG blob) — so the two postMessages stay in lockstep for index pairing.
      const stillCall = activeCaptureStill ? `; pyxel.screenshot(scale=${OGP_STILL_SCALE})` : ''
      ctx.pyodide.runPython(`import pyxel; pyxel.screencast(${scaleArg})${stillCall}`)
    }
    catch (e) {
      console.error('[useNativeScreencast] runPython trigger error:', e)
      // A PythonError means the game raised but Pyodide is alive; anything else is fatal.
      if ((e as { name?: string } | null)?.name !== 'PythonError') onPyxelFatal()
    }
  }

  /** Mode B: dispatch a synthetic Alt+3 keydown/keyup to the iframe document. */
  function triggerViaKeypress() {
    // #749 cross-origin: the parent can't reach the frame's document; ask the
    // frame to synthesize the Alt+3 keypress on its own document.
    if (activeFrameOrigin) {
      postCmd({ cmd: 'keypress', combo: 'alt+3', holdMs: activeKeypressHoldMs })
      return
    }
    const win = iframeEl?.contentWindow
    const doc = win?.document
    if (!win || !doc) return
    // Use the iframe's own KeyboardEvent so it's recognized in that context.
    const Kbd = (win as Window & typeof globalThis).KeyboardEvent
    const altDownInit: KeyboardEventInit = {
      key: 'Alt',
      code: 'AltLeft',
      keyCode: 18,
      which: 18,
      altKey: true,
      bubbles: true,
      cancelable: true,
      repeat: false,
    }
    const threeDownInit: KeyboardEventInit = {
      key: '3',
      code: 'Digit3',
      keyCode: 51,
      which: 51,
      altKey: true,
      bubbles: true,
      cancelable: true,
      repeat: false,
    }
    const threeUpInit: KeyboardEventInit = {
      key: '3',
      code: 'Digit3',
      keyCode: 51,
      which: 51,
      altKey: true,
      bubbles: true,
      cancelable: true,
    }
    const altUpInit: KeyboardEventInit = {
      key: 'Alt',
      code: 'AltLeft',
      keyCode: 18,
      which: 18,
      altKey: false,
      bubbles: true,
      cancelable: true,
    }
    try {
      doc.dispatchEvent(new Kbd('keydown', altDownInit))
      doc.dispatchEvent(new Kbd('keydown', threeDownInit))
      setTimeout(() => {
        try {
          doc.dispatchEvent(new Kbd('keyup', threeUpInit))
          doc.dispatchEvent(new Kbd('keyup', altUpInit))
        }
        catch (e) {
          console.error('[useNativeScreencast] keypress release error:', e)
        }
      }, activeKeypressHoldMs)
    }
    catch (e) {
      console.error('[useNativeScreencast] keypress dispatch error:', e)
    }
  }

  function autoTrigger() {
    if (!iframeEl) return
    if (activeTriggerMode === 'synthesizeKeypress') {
      triggerViaKeypress()
    }
    else {
      triggerViaRunPython()
    }
  }

  /**
   * Wait until Pyxel has finished booting (pyxelContext.initialized === true),
   * then start the auto-trigger cadence. This ensures the first clip covers a
   * full intervalMs window — without this delay, the first setInterval tick
   * fires before the user has clicked the "click to start" prompt, so the
   * buffer only has a fraction of a window's worth of frames.
   *
   * Uses a single setInterval (rather than recursive setTimeout) so the DevTools
   * async call stack stays flat. Bails out after INIT_WAIT_MAX_MS to avoid
   * polling forever if Pyxel never initializes (e.g. user navigated away or
   * the iframe broke).
   *
   * NOTE: do NOT issue any runPython here (e.g. reset_screencast). At the
   * moment `initialized` flips to true, pyxel.js is about to enter a
   * synchronous `pyodide.runPython(userCode)` to start the user's game (which
   * calls `pyxel.init()` and `pyxel.run()`). A nested runPython during that
   * window collides with the user code path and surfaces as a Pyodide fatal
   * error via `_displayFatalErrorOverlay`. The natural delay before the first
   * setInterval tick (intervalMs ms) is sufficient — by then the user's
   * pyxel.init() has long since returned.
   */
  const INIT_WAIT_POLL_MS = 250
  const INIT_WAIT_MAX_MS = 60_000
  function waitForInitThenStartCadence(intervalMs: number) {
    if (!isCapturing.value || !iframeEl) return
    pendingCadenceMs = intervalMs
    // #749 cross-origin: we can't read contentWindow.pyxelContext.initialized.
    // Wait for the frame's `__pyxelInited` (handled in onMessage → startCadence).
    // Send a requestInit in case the frame already booted before we subscribed;
    // guard with INIT_WAIT_MAX_MS so a frame that never inits still starts a cadence.
    if (activeFrameOrigin) {
      postCmd({ cmd: 'requestInit' })
      initWaitTimeout = setTimeout(() => {
        initWaitTimeout = null
        if (!autoTriggerTimer) {
          console.warn(
            '[useNativeScreencast] no __pyxelInited within',
            INIT_WAIT_MAX_MS / 1000,
            'seconds; starting cadence anyway',
          )
          startCadence()
        }
      }, INIT_WAIT_MAX_MS)
      return
    }
    const startedAt = Date.now()
    initWaitTimer = setInterval(() => {
      if (!isCapturing.value || !iframeEl) {
        if (initWaitTimer) clearInterval(initWaitTimer)
        initWaitTimer = null
        return
      }
      if (Date.now() - startedAt > INIT_WAIT_MAX_MS) {
        console.warn(
          '[useNativeScreencast] Pyxel init wait timed out after',
          INIT_WAIT_MAX_MS / 1000,
          'seconds; auto-trigger disabled',
        )
        if (initWaitTimer) clearInterval(initWaitTimer)
        initWaitTimer = null
        return
      }
      const ctx = (iframeEl.contentWindow as IframePyxelWindow | null)?.pyxelContext
      if (!ctx?.initialized || !ctx.pyodide?.runPython) return
      // Pyxel is ready — switch over to the actual capture cadence
      if (initWaitTimer) clearInterval(initWaitTimer)
      initWaitTimer = null
      autoTriggerTimer = setInterval(autoTrigger, intervalMs)
    }, INIT_WAIT_POLL_MS)
  }

  /** Begin listening for capture messages and (optionally) auto-triggering. */
  function startCapture(iframe: HTMLIFrameElement, opts: NativeScreencastOptions = {}) {
    if (isCapturing.value) return

    iframeEl = iframe
    isCapturing.value = true
    pyxelFailed.value = false
    screencasts.value = []
    screenshots.value = []

    activeTriggerMode = opts.triggerMode ?? options.triggerMode ?? 'runPython'
    activeKeypressHoldMs = opts.keypressHoldMs ?? options.keypressHoldMs ?? 50
    activeScale = opts.scale ?? options.scale ?? 1
    activeCaptureStill = opts.captureStill ?? options.captureStill ?? false
    activeFrameOrigin = opts.frameOrigin ?? options.frameOrigin ?? ''
    pendingCadenceMs = 0

    messageHandler = onMessage
    window.addEventListener('message', messageHandler)

    const intervalMs = opts.intervalMs ?? options.intervalMs ?? 0
    if (intervalMs > 0) {
      waitForInitThenStartCadence(intervalMs)
    }
  }

  /** Stop listening and clear the auto-trigger. Leaves accumulated clips intact. */
  function stopCapture() {
    isCapturing.value = false
    if (autoTriggerTimer) {
      clearInterval(autoTriggerTimer)
      autoTriggerTimer = null
    }
    if (initWaitTimer) {
      clearInterval(initWaitTimer)
      initWaitTimer = null
    }
    if (initWaitTimeout) {
      clearTimeout(initWaitTimeout)
      initWaitTimeout = null
    }
    if (messageHandler) {
      window.removeEventListener('message', messageHandler)
      messageHandler = null
    }
    iframeEl = null
    activeFrameOrigin = ''
    pendingCadenceMs = 0
  }

  onUnmounted(() => {
    if (isCapturing.value) stopCapture()
  })

  return {
    screencasts: readonly(screencasts),
    screenshots: readonly(screenshots),
    isCapturing: readonly(isCapturing),
    pyxelFailed: readonly(pyxelFailed),
    startCapture,
    stopCapture,
    /** Manually invoke a screencast trigger using the active mode. */
    triggerScreencast: autoTrigger,
    /** Mode A: directly call pyxel.screencast() via Pyodide. */
    triggerScreencastViaRunPython: triggerViaRunPython,
    /** Mode B: synthesize Alt+3 keypress on the iframe document. */
    triggerScreencastViaKeypress: triggerViaKeypress,
  }
}
